import classNames from 'classnames';

import { TableSkeletonCell } from '@ui/uikit/components/common/Table/TableSkeletonCell';

import { Typography } from '../Typography';
import { TableData, TableProps } from './Table';
import { TableBodyCell } from './TableBodyCell';
import { MobileLayoutType } from './TableHeader';
import { TableRow, TableRowProps } from './TableRow';

type TableBodyProps<Data extends TableData> = Required<
  Pick<TableProps<Data>, 'columns' | 'data' | 'topColumns' | 'isLoading'>
> &
  Pick<TableRowProps, 'style'> &
  MobileLayoutType;

export const TableBody = <Data extends TableData>({
  columns,
  data,
  style,
  isMobileLayout,
  topColumns,
  isLoading,
}: TableBodyProps<Data>) => {
  const filteredColumns = isMobileLayout
    ? columns.filter((col) => !topColumns.includes(col.name))
    : columns;

  return data.map((row, rowIndex) => (
    <div className={classNames(isMobileLayout && 'py-3 border-b border-base-border-medium')}>
      {isMobileLayout && (
        <TableRow style={style} key={`row-${rowIndex}-top`} hasBorder={false}>
          {topColumns.map((topColumnKey, colIndex) => {
            const column = columns.find((col) => col.name === topColumnKey);

            if (!column) {
              return null;
            }

            const { name, label, hasRightPadding, render } = column;

            const cellContent = render
              ? render({ row, rowIndex, colIndex, name, label })
              : row[name];

            const contentToRender = !isLoading ? cellContent : <TableSkeletonCell />;

            return (
              <TableBodyCell
                key={`row-${rowIndex}-col-${colIndex}-top`}
                hasRightPadding={hasRightPadding}
              >
                {contentToRender}
              </TableBodyCell>
            );
          })}
        </TableRow>
      )}

      <TableRow
        key={`row-${rowIndex}`}
        style={isMobileLayout ? {} : style}
        hasBorder={!isMobileLayout}
      >
        {filteredColumns.map((column, colIndex) => {
          const { name, label, hasRightPadding, render } = column;
          const isLastColumn = colIndex === columns.length - 1;
          const finalHasRightPadding = !isMobileLayout && (hasRightPadding ?? !isLastColumn);

          const cellContent = render ? render({ row, rowIndex, colIndex, name, label }) : row[name];

          const contentToRender = !isLoading ? cellContent : <TableSkeletonCell />;
          const labelToRender = !isLoading ? String(label) : <TableSkeletonCell />;

          return (
            <TableBodyCell
              key={`row-${rowIndex}-col-${colIndex}`}
              hasRightPadding={finalHasRightPadding}
            >
              {isMobileLayout ? (
                <div className="flex justify-between w-full items-start">
                  <Typography variant="caption-s" color="tertiary" textAlign="left" isFullWidth>
                    {labelToRender}
                  </Typography>
                  {contentToRender}
                </div>
              ) : (
                contentToRender
              )}
            </TableBodyCell>
          );
        })}
      </TableRow>
    </div>
  ));
};
